import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { useScoreApi } from "../api/scoreApi";
import { gateFailureReason } from "../util/gateFailureReason";
import { OUTCOME_KEYS, activeOutcomeKey } from "../util/rideOutcome";

const NORMAL = "normal";

// #2799. Replaces RideEditor's single EXCUSE toggle. Picks one of No Show /
// Withdrawn / Excused (each its own existing score-api POST) or Normal, which
// is the undo and calls the DELETE belonging to the outcome CURRENTLY set (see
// util/rideOutcome.js for why a generic clear would leave the stamp behind).
// A failure shows score-api's own message (never a status code); with no usable
// body it falls back to one generic line.
export default function SetOutcomeDialog({ open, ride, rideId, onClose, onChanged }) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();
  const current = activeOutcomeKey(ride) ?? NORMAL;
  const [choice, setChoice] = useState(current);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setChoice(activeOutcomeKey(ride) ?? NORMAL);
      setError(null);
    }
    // Re-seed only when the dialog opens; a background refetch must not
    // yank the admin's pick out from under them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const apply = async () => {
    setBusy(true);
    setError(null);
    try {
      if (choice === NORMAL) {
        const set = activeOutcomeKey(ride);
        if (set === OUTCOME_KEYS.NO_SHOW) await scoreApi.rides.undoRideNoShow(rideId);
        else if (set === OUTCOME_KEYS.WITHDRAWN) await scoreApi.rides.undoRideWithdrawn(rideId);
        else if (set === OUTCOME_KEYS.EXCUSED) await scoreApi.rides.undoRideExcused(rideId);
      } else if (choice === OUTCOME_KEYS.NO_SHOW) {
        await scoreApi.rides.markRideNoShow(rideId);
      } else if (choice === OUTCOME_KEYS.WITHDRAWN) {
        await scoreApi.rides.markRideWithdrawn(rideId);
      } else if (choice === OUTCOME_KEYS.EXCUSED) {
        await scoreApi.rides.markRideExcused(rideId);
      }
      await onChanged();
      onClose();
    } catch (err) {
      setError(gateFailureReason(err, t) ?? t("rideEditor.setOutcomeError"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} data-testid="set-outcome-dialog">
      <DialogTitle>{t("rideEditor.setOutcomeTitle")}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1 }}>{t("rideEditor.setOutcomeBody")}</DialogContentText>
        <RadioGroup value={choice} onChange={(e) => setChoice(e.target.value)} aria-label={t("rideEditor.setOutcomeTitle")}>
          <FormControlLabel
            value={OUTCOME_KEYS.NO_SHOW}
            control={<Radio inputProps={{ "data-testid": "outcome-no-show" }} />}
            label={t("rideEditor.outcomeNoShow")}
          />
          <FormControlLabel
            value={OUTCOME_KEYS.WITHDRAWN}
            control={<Radio inputProps={{ "data-testid": "outcome-withdrawn" }} />}
            label={t("rideEditor.outcomeWithdrawn")}
          />
          <FormControlLabel
            value={OUTCOME_KEYS.EXCUSED}
            control={<Radio inputProps={{ "data-testid": "outcome-excused" }} />}
            label={t("rideEditor.outcomeExcused")}
          />
          <FormControlLabel
            value={NORMAL}
            control={<Radio inputProps={{ "data-testid": "outcome-normal" }} />}
            label={t("rideEditor.outcomeNormal")}
          />
        </RadioGroup>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} data-testid="set-outcome-error">
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose} disabled={busy} data-testid="set-outcome-cancel">
          {t("rideEditor.setOutcomeCancel")}
        </Button>
        <Button
          variant="contained"
          loading={busy}
          disabled={choice === current}
          onClick={apply}
          data-testid="set-outcome-apply"
        >
          {t("rideEditor.setOutcomeApply")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
